import {REPOSITORY_SCOPES} from './repository-scope'

export const UI_READINESS_EVENT = 'one_copilot.web.client_readiness'

export const UI_READINESS_POLICY = {
  policy_version: 'client_readiness_v1',
  required_obligations: 'initialization|required_state|role_correct_input',
  gate_policy: 'one_copilot_chat',
  sampling_policy: 'all_flagged_eligible_v1',
} as const

export const UI_READINESS_SURFACES = [
  'global_task',
  'repository_task',
  'agents_page',
  'repository_agents',
  'new_repository_task',
  'global_overlay',
  'dashboard',
  'embedded_chat',
  'explain_error',
  'chat_app',
  'issue_panel',
  'memex_panel',
  'repository_panel',
  'unknown',
] as const

export type UiReadinessSurface = (typeof UI_READINESS_SURFACES)[number]
export const UI_READINESS_ENTRY_KINDS = ['new', 'active', 'resume', 'embedded', 'handoff'] as const
export const UI_READINESS_REPOSITORY_SCOPES = REPOSITORY_SCOPES
export type UiReadinessEntry = (typeof UI_READINESS_ENTRY_KINDS)[number]
/** How the view's route was reached. Reported on metrics only; Hydro facts are unchanged. */
export type UiReadinessNavigationType = 'hard' | 'soft' | 'handoff' | 'unknown'
export type UiReadinessRole = 'writable' | 'passive' | 'read_only' | 'unknown'
export type UiReadinessVisibility = 'visible' | 'hidden' | 'unknown'
export type UiReadinessStateSource = 'local' | 'snapshot' | 'action' | 'cache'
export type UiReadinessDependency = 'configuration' | 'environment' | 'session' | 'chat' | 'role'
export type UiReadinessFailure =
  | 'registration'
  | 'bootstrap'
  | 'hydration'
  | 'handoff'
  | 'session_mismatch'
  | 'role_mismatch'
  | 'controls_blocked'
  | 'product_timeout'
  | 'frontend_error'

export interface UiReadinessTime {
  readonly clockId: string
  readonly observedAtMs: number
  readonly monotonicMs: number
}

export interface UiReadinessOpen {
  readonly clientOpenId: string
  readonly surface: UiReadinessSurface
  readonly entryKind: UiReadinessEntry
  readonly repositoryScope: (typeof UI_READINESS_REPOSITORY_SCOPES)[number]
  readonly origin?: UiReadinessTime
  readonly originRequestId?: string
  readonly expectedTaskId?: string
  readonly navigationType?: UiReadinessNavigationType
}

export type UiReadinessBinding =
  | {readonly kind: 'new_session'}
  | {
      readonly kind: 'session'
      readonly sessionId: string
      readonly environmentId?: string
      readonly taskId?: string
      readonly chatId?: string
    }

interface UiReadinessFactBase {
  readonly policy_version: 'client_readiness_v1'
  readonly required_obligations: 'initialization|required_state|role_correct_input'
  readonly gate_policy: 'one_copilot_chat'
  readonly sampling_policy: 'all_flagged_eligible_v1'
  readonly event_id: string
  readonly client_open_id: string
  readonly client_view_id?: string
  readonly clock_id: string
  readonly sequence: number
  readonly observed_at_ms: number
  readonly monotonic_ms: number
  readonly origin_clock_id?: string
  readonly origin_observed_at_ms?: number
  readonly origin_monotonic_ms?: number
  readonly origin_request_id?: string
  readonly origin_request_id_source?: 'server_payload'
  readonly surface: UiReadinessSurface
  readonly entry_kind: UiReadinessEntry
  readonly repository_scope: UiReadinessOpen['repositoryScope']
  readonly role: UiReadinessRole
  readonly visibility: UiReadinessVisibility
  readonly evidence_source: 'origin_shell' | 'destination' | 'relay'
  readonly binding_kind?: UiReadinessBinding['kind']
  readonly session_id?: string
  readonly environment_id?: string
  readonly task_id?: string
  readonly chat_id?: string
}

export type UiReadinessObservation =
  | {readonly observation: 'open'}
  | {readonly observation: 'initialization_started'}
  | {
      readonly observation: 'initialization_completed'
      readonly initialization_timing: 'origin_clock'
      readonly initialization_elapsed_ms: number
      readonly initialization_local_elapsed_ms: number
    }
  | {
      readonly observation: 'initialization_completed'
      readonly initialization_timing: 'clock_unresolved'
      readonly initialization_local_elapsed_ms: number
    }
  | {
      readonly observation: 'required_state_available'
      readonly state_source: UiReadinessStateSource
      readonly binding_kind: UiReadinessBinding['kind']
    }
  | {
      readonly observation: 'input_ready'
      readonly state_event_id: string
      readonly state_to_input_elapsed_ms: number
      readonly binding_kind: UiReadinessBinding['kind']
    }
  | {readonly observation: 'dependency_wait_started'; readonly dependency: UiReadinessDependency}
  | {
      readonly observation: 'dependency_wait_ended'
      readonly dependency: UiReadinessDependency
      readonly dependency_wait_elapsed_ms: number
    }
  | {readonly observation: 'failure'; readonly reason: UiReadinessFailure}
  | {readonly observation: 'view_disposed'; readonly reason: 'closed' | 'replaced'}
  | {readonly observation: 'page_exit'; readonly delivery: 'best_effort'}

export type UiReadinessFact = UiReadinessFactBase & UiReadinessObservation

export interface UiReadinessState {
  readonly binding: UiReadinessBinding
  readonly role: Exclude<UiReadinessRole, 'unknown'>
  readonly source: UiReadinessStateSource
  readonly time: UiReadinessTime
}

export type UiReadinessControls =
  | {
      readonly role: 'writable'
      readonly binding: UiReadinessBinding
      readonly connected: boolean
      readonly editable: boolean
      readonly editHandlerAttached: boolean
      readonly actionHandlerAttached: boolean
      readonly validInputActionEnabled: boolean
    }
  | {
      readonly role: 'passive' | 'read_only'
      readonly binding: UiReadinessBinding
      readonly connected: boolean
      readonly editable: boolean
      readonly mutationActionsDisabled: boolean
    }

export function sameReadinessBinding(left: UiReadinessBinding, right: UiReadinessBinding): boolean {
  if (left.kind !== right.kind) return false
  if (left.kind === 'new_session' || right.kind === 'new_session') return true
  return (
    left.sessionId === right.sessionId &&
    left.environmentId === right.environmentId &&
    left.taskId === right.taskId &&
    left.chatId === right.chatId
  )
}
